import { useNavigate, useParams } from 'react-router-dom';
import { useWeddingVendors } from '../../hooks/useWeddingVendors';
import { useWeddingCategories } from '../../hooks/useWeddingCategories';
import { useWeddingLocation } from '../../context/WeddingLocationContext';
import LocationPicker from '../../components/weddings/LocationPicker';
import { ArrowLeft, SlidersHorizontal, MapPin } from 'lucide-react';
import '@material/web/chips/chip-set.js';
import '@material/web/chips/filter-chip.js';
import { useState, useMemo } from 'react';
import VendorCard from '../../components/weddings/VendorCard';

type SortOption = 'recommended' | 'rating-high' | 'rating-low';
type RatingFilter = 'all' | '4+' | '4.5+';

export default function VendorListPage() {
    const { category } = useParams<{ category: string }>();
    const navigate = useNavigate();
    const { location, setLocation } = useWeddingLocation();
    const { data: categories = [] } = useWeddingCategories();

    // Filters
    const [radius, setRadius] = useState(50);
    const [maxPrice, setMaxPrice] = useState<number>(Infinity); // Client-side filter
    const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all');
    const [sortBy, setSortBy] = useState<SortOption>('recommended');
    const [showFilters, setShowFilters] = useState(false);

    // Fetch vendors using Global Location + Category
    const { data: vendors = [], isLoading } = useWeddingVendors({
        categorySlug: category,
        lat: location?.lat,
        lon: location?.lon,
        radius: radius
    });

    const currentCategory = categories.find((c) => c.slug === category);

    const filteredAndSortedVendors = useMemo(() => {
        let result = [...vendors];

        // location filtering is handled by the hook/RPC now. 
        // We just handle client-side attribute filtering here.

        // Price filter (based on minimum package price)
        if (maxPrice !== Infinity) {
            result = result.filter((v) => {
                const minPackagePrice = v.packages && v.packages.length > 0
                    ? Math.min(...v.packages.map(p => p.price))
                    : null;
                // If no packages, we treat it as passing or failing? 
                // Let's include if price is not set (null) or <= maxPrice to be safe, 
                // or strict: only include if we know it's cheap enough.
                // Strict approach:
                return minPackagePrice !== null && minPackagePrice <= maxPrice;
            });
        }

        // Rating filter
        if (ratingFilter === '4+') {
            result = result.filter((v) => v.rating >= 4);
        } else if (ratingFilter === '4.5+') {
            result = result.filter((v) => v.rating >= 4.5);
        }

        // Sorting
        switch (sortBy) {
            case 'rating-high':
                result.sort((a, b) => b.rating - a.rating);
                break;
            case 'rating-low':
                result.sort((a, b) => a.rating - b.rating);
                break;
            case 'recommended':
            default:
                // Default: sort by distance if location is set? 
                // RPC usually sorts by distance. If not, we trust RPC order unless overridden.
                break;
        }

        return result;
    }, [vendors, maxPrice, ratingFilter, sortBy]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <button
                        onClick={() => navigate('/weddings')}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft size={20} />
                        Back to Categories
                    </button>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                {currentCategory?.name || 'All Vendors'}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mt-1">
                                {filteredAndSortedVendors.length} vendors found
                            </p>
                        </div>

                        {/* Sort Dropdown */}
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="lg:hidden flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300"
                            >
                                <SlidersHorizontal size={18} />
                                Filters
                            </button>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500"
                            >
                                <option value="recommended">Recommended</option>
                                <option value="rating-high">Rating: High to Low</option>
                                <option value="rating-low">Rating: Low to High</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Filters Sidebar */}
                    <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 sticky top-24">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Filters</h3>

                            <div className="space-y-6">
                                {/* Location Filter */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                        <MapPin size={16} /> Location
                                    </h3>
                                    <div className="space-y-3">
                                        <LocationPicker
                                            onLocationSelect={(lat, lon, address) => {
                                                if (lat && lon) {
                                                    setLocation({ lat, lon, address: address || '' });
                                                } else {
                                                    setLocation(null);
                                                }
                                            }}
                                            initialAddress={location?.address}
                                            placeholder="City or Zip..."
                                        />

                                        {location && (
                                            <div className="animate-in fade-in slide-in-from-top-1 space-y-1">
                                                <div className="flex justify-between text-xs text-gray-500">
                                                    <span>Radius</span>
                                                    <span>{radius} km</span>
                                                </div>
                                                <input
                                                    type="range"
                                                    min="5"
                                                    max="200"
                                                    step="5"
                                                    value={radius}
                                                    onChange={(e) => setRadius(parseInt(e.target.value))}
                                                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Price Filter */}
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Price</h3>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                                            <span>Max Price</span>
                                            <span>{maxPrice === Infinity ? 'Any' : `₹${maxPrice.toLocaleString()}`}</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="200000"
                                            step="5000"
                                            value={maxPrice === Infinity ? 200000 : maxPrice}
                                            onChange={(e) => {
                                                const val = parseInt(e.target.value);
                                                setMaxPrice(val === 200000 ? Infinity : val);
                                            }}
                                            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
                                        />
                                        <div className="flex justify-between text-xs text-gray-400">
                                            <span>₹0</span>
                                            <span>₹2L+</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Rating Filter */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Rating
                                    </label>
                                    <div className="flex flex-col gap-2">
                                        {[
                                            { value: 'all', label: 'All Ratings' },
                                            { value: '4+', label: '4+ Stars' },
                                            { value: '4.5+', label: '4.5+ Stars' },
                                        ].map((option) => (
                                            <button
                                                key={option.value}
                                                onClick={() => setRatingFilter(option.value as RatingFilter)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${ratingFilter === option.value
                                                    ? 'bg-rose-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Category Chips */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Category
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {categories.map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => navigate(`/weddings/vendors/${cat.slug}`)}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${cat.slug === category
                                                    ? 'bg-rose-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {cat.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Clear Filters */}
                                {(location || ratingFilter !== 'all' || maxPrice !== Infinity) && (
                                    <button
                                        onClick={() => {
                                            setLocation(null);
                                            setRadius(50);
                                            setRatingFilter('all');
                                            setMaxPrice(Infinity);
                                        }}
                                        className="w-full px-4 py-2 text-rose-600 border border-rose-600 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors text-sm font-medium"
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    </aside>

                    {/* Vendors Grid */}
                    <main className="flex-1">
                        {isLoading ? (
                            <div className="text-center py-12 text-gray-500">Loading vendors...</div>
                        ) : filteredAndSortedVendors.length === 0 ? (
                            <div className="text-center py-12">
                                <p className="text-gray-500 dark:text-gray-400">No vendors found matching your filters.</p>
                                <button
                                    onClick={() => {
                                        setLocation(null);
                                        setRadius(50);
                                        setRatingFilter('all');
                                        setMaxPrice(Infinity);
                                    }}
                                    className="mt-4 text-rose-600 hover:text-rose-700 font-medium"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {filteredAndSortedVendors.map((vendor) => (
                                    <div key={vendor.id} className="h-full">
                                        <VendorCard vendor={vendor} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}
